/*
 * Drag & drop of a gallery multi-selection onto a book page.
 *
 * The dropped photos become the whole content of one page side : we pick a page template of
 * the current theme with the same photo count, else a generated layout. Commit and live drag
 * preview share _resolveDrop / _layoutForDrop / _simulateDrop, so the preview matches the result.
 *
 * Owned by BookBoard (created in its create()) ; every board member is reached through `this.board`.
 */
fx.ns("desktop.book.edition", class PhotosDropper extends fx.EventDispatcher
{
    constructor(board)
    {
        super();
        this.board = board;

        // warm the theme media early : selecting via the gallery checkboxes fires no event we could hook
        this.delay(this.preloadThemeMedia, 3000);
    }


    get maxDroppedPhotos()
    {
        return fx.get("settings").maxPhotosPerSide;   // shared with SelectablePhotoGrid.maxSelection
    }

    // Target area, side, photos and page geometry for a drop — null when the drop isn't allowed.
    _resolveDrop(data)
    {
        if (!data || !data.contents) return null;

        let area = data.area ? this.board.getAreaForPage(data.area) : this.board.currentArea;
        if (!area || !area.data || !area.page) return null;

        let side = data.side || area.activePart ||
                   (area.page.isCover || area.page.isFirstPage ? "right" : "left");

        // a spine drop belongs to the front cover, else applyLayout spreads it over both
        if (side == "center") side = "right";

        // the inside cover isn't printable : drop nothing rather than spill onto the facing page
        if (side == "left" && area.page.isFirstPage) return null;
        if (side == "right" && area.page.isLastPage) return null;

        let photos = [];
        for (let i = 0; i < data.contents.length; i++)
        {
            let photo = this.board.model.photos.find({id : data.contents[i]});
            if (photo) photos.add(photo, true);   // dedup, keep gallery order
        }

        if (photos.length == 0) return null;

        return {
            area       : area,
            photos     : photos.slice(0, this.maxDroppedPhotos),
            side       : side,
            // one page wide ; on the cover the slice acts as the gap between the two pages
            pageWidth  : this.board.book.pageWidth,
            pageHeight : area.pageHeight,
            extraWidth : area.page.isCover ? this.board.book.sliceWidth : (area.extraWidth || 0)
        };
    }

    // Theme page template for this drop, or null — never on the cover, templates are interior pages.
    _themeTemplateFor(drop)
    {
        return drop.area.page.isCover ? null
                                      : this._findThemeLayout(drop.photos.length, drop.side);
    }

    // Layout to use : theme template (keepDesign false, keeps its own z-order) else generated (keepDesign true).
    _layoutForDrop(drop, skipTemplate)
    {
        let template = skipTemplate ? null : this._themeTemplateFor(drop);

        if (template && this._templateTakesEveryPhoto(drop, template))
            return { layout : template, keepDesign : false };

        // the fold is on the opposite edge from the side this page sits on -- the cover folds too, on its spine
        let fold = drop.side == "left" ? "right" : drop.side == "right" ? "left" : null;
        let layouts = fx.service("LayoutsGenerator").getLayouts(drop.photos, fold);

        // keepDesign false : the drop clears the side (photos, stickers AND text), like the theme path
        return (layouts && layouts.length) ? { layout : layouts[0], keepDesign : false } : null;
    }

    // Would this template really take every photo ? Checked on a copy, so a bad one never touches the page.
    _templateTakesEveryPhoto(drop, template)
    {
        try
        {
            let dry = this._simulateDrop(drop, template, false);
            return this._countPlaced(dry, drop.photos) >= drop.photos.length;
        }
        catch (e)
        {
            return true;   // the check is only a safety net
        }
    }

    // Single-page template of the current theme with `count` photo slots, or null.
    _findThemeLayout(count, side)
    {
        let themeid = this.board.model.theme && this.board.model.theme.id;
        if (!themeid || !this.board.model.layouts || !this.board.model.layouts.filter) return null;

        let pageWidth = this.board.book.pageWidth;
        let tolerance = 1;   // rounding

        // the live list : the simpleThemeLayouts getter memoises and misses later-loaded templates
        let matches = this.board.model.layouts.filter(function(layout) {
            if (!layout || layout.themeid != themeid || layout.typeid != 1) return false;
            if (layout.num_photos != count || layout.accountid != 0) return false;

            // double-page templates keep spread coordinates, so their slots would miss this side
            if (layout.doublepage && layout.doublepage != "0") return false;

            // a template sticking out of the page gets cut, by the gutter or by the outer edge
            let components = layout.components;
            for (let i = 0; i < components.length; i++)
            {
                let c = components.getItemAt ? components.getItemAt(i) : components[i];
                if (!c || c.isBackground) continue;   // backgrounds cover the page by design

                let x = Number(c.x), width = Number(c.width);
                if (isNaN(x) || isNaN(width)) continue;

                if (x < -tolerance) return false;
                if (x + width > pageWidth + tolerance) return false;
            }

            return true;
        });

        return matches.length ? matches[0] : null;
    }

    // The page as the drop would leave it, on a detached copy : same code as the commit, no model or history touched.
    _simulateDrop(drop, layout, keepDesign)
    {
        let source = drop.area.data;
        let page = new models.ProductBookPage({ typeid       : source.typeid,
                                                number       : source.number,
                                                hasbackcover : source.hasbackcover }, true);

        for (let i = 0; i < source.components.length; i++)
        {
            let c = source.components.getItemAt(i);
            if (!c || c.deleted) continue;
            page.components.addItem(new models.CreationElementContent(c.JSON()), false, true, true);
        }

        this._applyLayoutTo(page, drop, layout, keepDesign);

        return page;
    }

    // addPhoto consumes the array (shift) : always give it a copy.
    _applyLayoutTo(page, drop, layout, keepDesign)
    {
        page.addPhoto(drop.photos.slice(), drop.side, drop.pageWidth, drop.pageHeight,
                      drop.extraWidth, layout, keepDesign);
    }

    // How many dropped photos actually landed — counted by id, so it doesn't depend on applyLayout's branch.
    _countPlaced(page, photos)
    {
        let wanted = {}, seen = {}, placed = 0;

        for (let i = 0; i < photos.length; i++)
            wanted[photos[i].id] = true;

        let components = page.photosComponents;
        for (let i = 0; i < components.length; i++)
        {
            let c = components[i];
            if (!c || c.deleted || !c.content) continue;

            if (wanted[c.content] && !seen[c.content])
            {
                seen[c.content] = true;
                placed++;
            }
        }

        return placed;
    }

    // Which page a component sits on, by its center — a full-bleed slot may start before the boundary.
    _isOnSide(component, side, pageWidth)
    {
        let bounding = fx.graphics.getBoundingRect(component, component.rotation);
        let center = bounding.x + bounding.width / 2;

        return (side == "left") ? (center < pageWidth) : (center >= pageWidth);
    }


    dropPhotos(data)
    {
        let drop = this._resolveDrop(data);
        if (!drop) return;

        // flush a drop still waiting for media before currentArea moves, so it isn't lost
        if (this._pendingDrop)
        {
            let previous = this._pendingDrop;
            this._pendingDrop = null;
            this._applyDrop(previous.drop, true);   // no template : its media never arrived
        }

        // aim the board at the page we mutate, else undo() early-returns and merely scrolls
        this.board.currentArea = drop.area;
        drop.area.initLayoutData();

        // a template's background and stickers must be loaded first, or they render blank
        let template = this._themeTemplateFor(drop);
        let missing = template ? (drop.area.getMediasToLoad(template) || []) : [];

        if (missing.length)
        {
            this._pendingDrop = { drop : drop, template : template, tries : 0 };
            this.board.model.loadLayoutMedias(missing);
            this.delay(this._awaitTemplateMedia, 350);
            return;
        }

        this._applyDrop(drop);
    }

    // Wait for the template media, then apply — bounded : give up on the template, never on the photos.
    _awaitTemplateMedia()
    {
        let pending = this._pendingDrop;
        if (!pending) return;

        let ready;
        try { ready = !(pending.drop.area.getMediasToLoad(pending.template) || []).length; }
        catch (e) { ready = true; }

        let gaveUp = (++pending.tries >= 12);

        if (ready || gaveUp)
        {
            this._pendingDrop = null;
            this._applyDrop(pending.drop, gaveUp && !ready);
            return;
        }

        this.delay(this._awaitTemplateMedia, 350);   // one bound method : no bind buildup
    }

    _applyDrop(drop, skipTemplate)
    {
        try
        {
            let choice = this._layoutForDrop(drop, skipTemplate);
            if (choice)
                this._applyLayoutTo(drop.area.data, drop, choice.layout, choice.keepDesign);
        }
        catch (e) {}

        // always clear : a failure must not leave the gallery selection stuck
        this.board.model.creation.event("clear-photos-selection");
    }

    // Warm the theme templates' media once, so the preview can draw their background from the first hover.
    preloadThemeMedia()
    {
        try
        {
            let area = this.board.currentArea;
            let themeid = this.board.model.theme && this.board.model.theme.id;

            if (!area || !area.getMediasToLoad || !themeid ||
                !this.board.model.layouts || !this.board.model.layouts.filter) return;

            // keyed on the theme : an early call that warmed nothing must not disable it for good
            if (this._themeMediaPreloadedFor == themeid) return;

            let templates = this.board.model.layouts.filter(function(layout) {
                return layout && layout.themeid == themeid && layout.typeid == 1 &&
                       !(layout.doublepage && layout.doublepage != "0");
            });

            let medias = [], seen = {};
            for (let i = 0; i < templates.length && medias.length < 60; i++)
            {
                let needed = area.getMediasToLoad(templates[i]) || [];

                for (let j = 0; j < needed.length; j++)
                {
                    let key = needed[j].type + ":" + needed[j].id;
                    if (seen[key]) continue;

                    seen[key] = true;
                    medias.push(needed[j]);
                }
            }

            if (!medias.length) return;

            this._themeMediaPreloadedFor = themeid;

            if (this.board.model.preloadLayoutMedias)
                this.board.model.preloadLayoutMedias(medias);
            else
                this.board.model.loadLayoutMedias(medias);
        }
        catch (e) {}
    }



    // Shows what the drop would produce, in a throwaway stage over the area — view-only, never touches the model.
    showPreview(data)
    {
        try
        {
            this.hidePreview();
            this.preloadThemeMedia();          // one-shot guarded

            let drop = this._resolveDrop(data);
            if (!drop || !drop.area.stageContainer) return;

            let choice = this._layoutForDrop(drop);
            if (!choice) return;

            let area = drop.area, side = drop.side, pageWidth = drop.pageWidth;
            let result = this._simulateDrop(drop, choice.layout, choice.keepDesign);

            // keep the hovered side's new content ; the real one underneath is hidden below
            let previewPage = new models.ProductBookPage({ typeid : area.data.typeid }, true);
            let shown = 0;

            for (let i = 0; i < result.components.length; i++)
            {
                let c = result.components.getItemAt(i);
                if (!c || c.deleted) continue;

                if (c.isBackground)
                {
                    // only once cached : an unresolved background paints an opaque white box
                    if (c.creation != side || !c.content) continue;
                    if (!this.board.model.backgrounds || !this.board.model.backgrounds.find({ id : c.content })) continue;
                }
                else
                if (!this._isOnSide(c, side, pageWidth))
                {
                    continue;
                }
                else
                {
                    shown++;
                }

                previewPage.components.addItem(new models.CreationElementContent(c.JSON()), false, true, true);
            }

            if (shown == 0) return;

            // registered BEFORE loading : if rendering throws, hidePreview can still remove it
            let overlay = new shared.stage.Stage(previewPage, this.board.model, this.board.componentFactory, side, true);
            area.stageContainer.addChild(overlay, { top : 0, left : 0, width : area.width(), height : area.height() });

            this._previewOverlay = overlay;
            this._previewArea = area;
            this._previewPage = previewPage;

            overlay.scale = area.scale;
            overlay.load(previewPage, side);
            overlay.css({ "pointer-events" : "none", "z-index" : 5, "background-color" : "transparent" });

            // hide the real content of that side so the preview doesn't stack over it
            this._previewHidden = [];
            let realChilds = area.stage.getChilds();
            for (let i = 0; i < realChilds.length; i++)
            {
                let c = realChilds.getItemAt(i);
                if (!c || !c.data || c.data.isBackground) continue;

                let b = fx.graphics.getBoundingRect(c.data, c.data.rotation);
                let center = b.x + b.width / 2;
                let onSide = (side == "left") ? (center < pageWidth) : (center >= pageWidth);
                if (onSide)
                {
                    c._previewHidden = true;   // marker : the sweep below restores only these
                    c.hide();
                    this._previewHidden.push(c);
                }
            }
        }
        catch (e)
        {
            this.hidePreview();
        }
    }

    hidePreview()
    {
        if (this._previewHidden)
        {
            // per-item guard : one failed show() must not strand the other photos hidden
            for (let i = 0; i < this._previewHidden.length; i++)
                try {
                    let c = this._previewHidden[i];
                    if (c && c.show)
                    {
                        c._previewHidden = false;
                        c.show();
                    }
                } catch (e) {}
        }

        this._previewHidden = null;

        try
        {
            if (this._previewOverlay)
            {
                if (this._previewArea && this._previewArea.stageContainer && this._previewArea.stageContainer.removeChild)
                    this._previewArea.stageContainer.removeChild(this._previewOverlay, true);
                else if (this._previewOverlay.destroy)
                    this._previewOverlay.destroy();
            }
        }
        catch (e) {}

        // Safety net : sweep EVERY area for a leftover preview stage and for any component still
        // hidden by a preview. Tracked references can be lost (a throw mid-render, an area
        // reload), and a stranded overlay looks exactly like a second layout stacked on the page
        // while the real photos underneath stay invisible.
        try
        {
            let areas = this.board.areas;
            for (let i = 0; areas && i < areas.length; i++)
            {
                let a = areas.getItemAt ? areas.getItemAt(i) : areas[i];
                if (!a || !a.stageContainer || !a.stageContainer.getChilds) continue;

                let childs = a.stageContainer.getChilds();
                for (let j = childs.length - 1; j >= 0; j--)
                {
                    let c = childs.getItemAt ? childs.getItemAt(j) : childs[j];
                    if (c && c !== a.stage && c instanceof shared.stage.Stage)
                        try { a.stageContainer.removeChild(c, true); } catch (e) {}
                }

                if (!a.stage || !a.stage.getChilds) continue;

                let real = a.stage.getChilds();
                for (let j = 0; j < real.length; j++)
                {
                    let c = real.getItemAt ? real.getItemAt(j) : real[j];
                    if (c && c._previewHidden && c.show)
                        try { c._previewHidden = false; c.show(); } catch (e) {}
                }
            }
        }
        catch (e) {}

        this._previewOverlay = null;
        this._previewArea = null;
        this._previewPage = null;
    }
});
